import type { CardCategory, CardDef, NodeKind } from "@reagent/sim";
import { ALL_CARDS } from "@reagent/content";
import { assertNever } from "@reagent/sim";
import { PALETTE } from "../../render/palette";

/**
 * Presentation for the game's cards.
 *
 * The catalogue lives in @reagent/content and is the source of truth. This
 * module adds only what the simulation has no opinion about: prose, accent
 * colour, and how a card is aimed at the board.
 *
 * Reading content is not a breach of the driver boundary — these are content
 * definitions, not driver state, and the UI still learns nothing about which
 * SimDriver it is talking to.
 */

export type { CardCategory };

/** How a card is committed to the board. Derived, never hand-maintained. */
export type CardTarget = "tile" | "node" | "pipe" | "instant";

export interface CardView {
  readonly id: string;
  readonly name: string;
  readonly category: CardCategory;
  readonly costKw: number;
  readonly blurb: string;
  readonly target: CardTarget;
  /** Hardware only: what the placement ghost should stand in for. */
  readonly deploys: NodeKind | null;
  /** False for a hazard card that is a pure dead draw (spec 0007 §4). */
  readonly isPlayable: boolean;
}

export const CARD_CATEGORY_COLOR: Record<CardCategory, string> = {
  hardware: PALETTE.sage,
  metaUtility: PALETTE.fluidMagenta,
  directive: PALETTE.fluidSkyBlue,
  attachment: PALETTE.mustard,
  hazard: PALETTE.terracotta,
};

/**
 * Flavour text, keyed by card id. Deliberately describes the *documented
 * mechanic* rather than a vibe — a player reading "sheds 1 heat every tick"
 * can reason about the 2-tick explosion clock; one reading "keeps things cool"
 * cannot.
 */
const BLURBS: Record<string, string> = {
  // Hardware — pipes
  "card-pipe-glass": "Links two machines, 1 unit a tick. Corrosives eat 10 HP a tick.",
  "card-pipe-ptfe": "Lined line. Immune to corrosion, cheaper than alloy.",
  "card-pipe-hastelloy": "Alloy line. Shrugs off corrosives entirely — and costs for it.",
  "card-emergency-bypass":
    "Glass line, playable mid-turn — the one hardware exception to Setup's routing rule, for a route you didn't plan for.",

  // Hardware — machines
  "card-mixer": "Combines inbound reagents. Runs hot when the reaction is exothermic.",
  "card-mixer-heavy": "A mixer that takes 8 heat before it complains, and holds 4 units.",
  // Hardware — recipe-bound reactors (spec 0068). Each names what its recipe makes;
  // an install may pick a different reactor recipe, so the offer says so too.
  "card-tp-maceration-vat": "A reactor bound to maceration: makes CrudeDye. Pick another reactor recipe at install.",
  "card-tp-dip-vat": "A reactor bound to the dip: makes DipVat. Pick another reactor recipe at install.",
  "card-gf-blend": "A reactor bound to the blend: makes GreekFire. Pick another reactor recipe at install.",
  "card-gf-siphon": "A reactor bound to the siphon: makes SeaFire. Pick another reactor recipe at install.",
  "card-gl-melt": "A furnace bound to the melt: makes Glass and Brine. Pick another reactor recipe at install.",
  "card-gl-colour": "A furnace bound to colouring: makes ColouredGlass. Pick another reactor recipe at install.",
  "card-mt-smelter": "A smelter bound to smelting: makes Copper and Oxide. Takes 8 heat, holds 4 units.",
  "card-mt-cementation": "A reactor bound to cementation: makes Charge. Holds 4 units.",
  "card-pf-steep": "A reactor bound to the steep: makes Rhodinon and Alkali. Takes only 2 heat, holds 4 units.",
  "card-pf-salve": "A reactor bound to the salve: makes RawSalve. Pick another reactor recipe at install.",
  "card-splitter":"Feeds its outputs in turn, one packet each.",
  "card-pump": "Pushes one extra packet into a full downstream buffer. The line still counts the tick as blocked.",
  "card-catalytic-filter": "Doubles reaction yield — until impure feed poisons the bed.",
  "card-storage-tank": "Parks 3 units and relieves backlog on the line behind it.",
  "card-surge-tank": "A storage tank that holds 8. Buys a bottleneck real room.",
  "card-product-hopper": "Collects finished product. Anything that lands here is scored.",
  "card-vent": "Flares whatever reaches it. Fined for it, but less than dumping.",
  "card-phase-converter": "Splits hot streams by volatility — some fractions boil off, others condense.",
  "card-incubator": "Holds packets for a recipe-defined duration before release.",
  "card-chiller": "Lowers packet temperature 30°C every tick — cools overheating streams.",
  "card-heater": "Raises packet temperature 30°C every tick — builds heat toward boiling points.",
  "card-emergency-buffer":
    "A flow buffer, playable mid-turn — the same routing exception as the Emergency Bypass Pipe.",

  // Attachments
  "card-lid": "Seals a waste buyer. Without one, it draws vermin — 1 risk added per turn.",
  "card-cooling-jacket": "Snap onto hardware. Sheds 1 heat every tick.",
  "card-cooling-jacket-twin": "Sheds 2 heat a tick — enough for the hottest reactions.",
  "card-ptfe-lining": "Lines every standard pipe leaving this machine. Stops corrosion dead.",
  "card-burst-disk": "Buys its lines 2 more pressure before they let go.",
  "card-guard-bed": "Absorbs the next 3 impure packets before the catalyst takes any.",
  "card-polishing-filter": "Absorbs the next 5 impure packets. One bed protector per machine.",
  "card-insulation": "Raises this machine's critical temperature by 3.",
  "card-surge-drum": "Adds 3 units of buffer, so the line behind stops backing up.",

  // Directives
  "card-flash-freeze": "Clears every heat counter on the rig at once.",
  "card-solvent-flush": "Dissolves sludge and resets every residence clock.",
  "card-nitrogen-purge": "Bleeds the pressure off every intact line.",
  "card-emergency-flush": "Dumps every buffer. Clears any blockage — and loses the material.",
  "card-patch-weld": "Puts 60 HP of wall back on every damaged line.",
  "card-catalyst-recharge": "Fresh catalyst bed. Back to full yield.",
  "card-fumigate": "Clears an infestation on one machine.",
  "card-stir-the-vat": "Resets one incubator's residence clock, keeping its hold progress.",
  "card-low-fire": "One reactor fires at most one multiple until the end of the turn.",
  "card-overtime": "+3 kW for this Play phase only.",
  "card-rush-order": "Draw 2 more cards this turn.",
  "card-scrub": "Scrubs one contaminated tile clean for 2 kW. Play it on the tile.",

  // Facility mitigations (spec 0028) — Phase 1 installs that answer §3 facility hazards
  "card-hazmat-absorbent": "One-shot install. The first grid contamination clears at the end of the tick it hit.",
  "card-surge-protector": "Prevents a power surge from freezing its node. The surge still fires.",
  "card-emergency-power-cell": "One-shot install. The first blackout turn this stage recovers 2 kW.",
  "card-facility-mousetrap": "Infested machines lose 1 tick in 4, not 1 in 2. Pest risk accrual is halved.",
  "card-hvac-chillers": "A facility heatwave adds no heat. Reaction heat still sheds normally.",
  "card-seismic-mounts": "Seismic events add no pressure to the rig's pipes.",

  // Hazards — what neglect puts in your deck (spec 0007 §4: nine cards, each
  // its own differentiated deck impact rather than a shared bulk-damage
  // counter). Cards marked isPlayable: false say so in words, per AC20 — a
  // player reading the hand should never have to discover "dead draw" by
  // trying to play one.
  "card-thermal-shock":
    "Unplayable — a dead draw that bleeds 1 kW off every turn's budget while it sits in hand.",
  "card-line-leak": "Unplayable — a dead draw that costs 1 card off your next hand if left unplayed.",
  "card-corrosive-pitting":
    "Unplayable — a dead draw that adds 1 kW to every Directive you play this turn, per copy held.",
  "card-catalyst-deactivation":
    "Unplayable — a dead draw that costs 2 Credits in maintenance at the end of the stage if left unstripped.",
  "card-clogged-line": "Locks the tile it was drawn for. Play it there to clear the lock and the card.",
  "card-chemical-spill":
    "Locks a hand slot shut. Play it on the contaminated tile for 2 kW to clear the tile and the slot.",
  "card-power-outage": "Unplayable — a dead draw that exhausts every remaining kW the moment Play begins.",
  "card-rodent-infestation":
    "Unplayable — a dead draw. Warns of an infestation: the affected machine suffers a slowdown, working only every other tick.",
  "card-structural-shear":
    "Unplayable — a dead draw that strips 10% off every glass pipe's HP, every turn it sits in hand.",
};

/**
 * Targeting falls out of the engine definition, so a new card cannot be
 * playable in the engine but unaimed in the UI.
 */
export function targetOf(def: CardDef): CardTarget {
  if (def.deploysPipe) return "pipe";
  if (def.deploysNode) return "tile";
  if (def.attachment) return "node";
  if (def.directive) {
    const directive = def.directive;
    switch (directive.kind) {
      case "scrubTile":
        return "tile";
      case "fumigate":
      case "stirVat":
      case "lowFire":
        return "node";
      case "flashFreeze":
      case "solventFlush":
      case "nitrogenPurge":
      case "emergencyFlush":
      case "patchWeld":
      case "catalystRecharge":
      case "overtime":
      case "rushOrder":
        return "instant";
      default:
        return assertNever(directive, "targetOf");
    }
  }
  return "instant";
}

function toView(def: CardDef): CardView {
  return {
    id: def.id,
    name: def.name,
    category: def.category,
    costKw: def.costKw,
    blurb: BLURBS[def.id] ?? `${def.category} card.`,
    target: targetOf(def),
    deploys: def.deploysNode ?? null,
    isPlayable: def.isPlayable ?? true,
  };
}

export const CARD_VIEWS: Record<string, CardView> = Object.fromEntries(
  ALL_CARDS.map((def) => [def.id, toView(def)]),
);

export function getCardView(id: string): CardView | null {
  return CARD_VIEWS[id] ?? null;
}
